// src/components/AnalyticsDrillModal.js — explore rows behind Analytics KPIs
import { useMemo, useState, useEffect } from "react";
import { startOfMonth, endOfMonth } from "date-fns";
import {
  getPaymentTimestampMs,
  paymentAmountUsd,
  isSuccessfulPayment,
  paidContactIdSet,
} from "../lib/metrics";
import { funnelDrillContent, downloadCohortCsv, COHORT_CSV_EXPORT_KEYS } from "../lib/productAnalyticsMetrics";
import { geoConcentrationDrillContent } from "../lib/workspaceGeoAnalytics";
import { growthDrillContent } from "../lib/growthAnalyticsMetrics";
import {
  computeDeviceAnalytics,
  deviceDrillContent,
  downloadDeviceSegmentCsv,
  DEVICE_SEGMENT_EXPORT_KEYS,
} from "../lib/deviceAnalyticsMetrics";
import {
  activationSankeyDrillContent,
  downloadActivationSankeyCsv,
  ACTIVATION_SANKEY_EXPORT_KEYS,
} from "../lib/activationSankeyMetrics";

function docCreatedMs(d) {
  const v = d?.createdAt ?? d?.created_at ?? d?.created ?? d?.timestamp;
  if (v == null) return 0;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (v instanceof Date) return v.getTime();
  if (typeof v === "object" && typeof v.seconds === "number") return v.seconds * 1000;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function payEmail(p) {
  return p?.email || p?.customer_email || p?.user_email || "—";
}

export default function AnalyticsDrillModal({
  open,
  drill,
  onClose,
  contacts,
  payments,
  orders,
  scans,
  files = [],
  workspaces,
  workspaceMembers,
  productAnalyticsEvents = [],
  growth,
  onOpenContact,
  onGoFinances,
}) {
  const [geoLevel, setGeoLevel] = useState("country");

  useEffect(() => {
    if (drill?.type === "geo_concentration") {
      setGeoLevel(drill.level || "country");
    }
  }, [drill]);

  const geoTable = useMemo(() => {
    if (!drill || drill.type !== "geo_concentration") return null;
    return geoConcentrationDrillContent(geoLevel, workspaces);
  }, [drill, geoLevel, workspaces]);

  const funnelTable = useMemo(() => {
    if (!drill || drill.type !== "funnel_drill" || !drill.funnelKey) return null;
    return funnelDrillContent(drill.funnelKey, contacts, scans, orders, payments, files, productAnalyticsEvents);
  }, [drill, contacts, scans, orders, payments, files, productAnalyticsEvents]);

  const growthTable = useMemo(() => {
    if (!drill || drill.type !== "growth_drill" || !drill.growthKey || !growth) return null;
    return growthDrillContent(drill.growthKey, growth, contacts);
  }, [drill, growth, contacts]);

  const deviceAnalytics = useMemo(() => computeDeviceAnalytics(contacts), [contacts]);

  const deviceTable = useMemo(() => {
    if (!drill || drill.type !== "device_drill" || !drill.deviceKey) return null;
    const opts = { includeInternal: Boolean(drill.includeInternal) };
    return deviceDrillContent(drill.deviceKey, deviceAnalytics, contacts, drill.filterLabel || "", opts);
  }, [drill, deviceAnalytics, contacts]);

  const activationTable = useMemo(() => {
    if (!drill || drill.type !== "activation_drill" || !drill.stageKey) return null;
    return activationSankeyDrillContent(
      drill.stageKey,
      contacts,
      workspaces,
      workspaceMembers,
      files,
      orders,
      scans,
      productAnalyticsEvents,
      drill.dateRange || null
    );
  }, [drill, contacts, workspaces, workspaceMembers, files, orders, scans, productAnalyticsEvents]);

  const rows = useMemo(() => {
    if (!drill) return [];
    if (drill.type === "geo_concentration") {
      return (geoTable?.rows ?? []).map((r) => ({
        k: r.k,
        cells: r.cells,
        contact: null,
      }));
    }
    if (drill.type === "funnel_drill") {
      return (funnelTable?.rows ?? []).map((r) => ({
        k: r.k,
        cells: r.cells,
        contact: r.contact ?? null,
      }));
    }
    if (drill.type === "growth_drill") {
      return (growthTable?.rows ?? []).map((r) => ({
        k: r.k,
        cells: r.cells,
        contact: r.contact ?? null,
      }));
    }
    if (drill.type === "device_drill") {
      return (deviceTable?.rows ?? []).map((r) => ({
        k: r.k,
        cells: r.cells,
        contact: r.contact ?? null,
      }));
    }
    if (drill.type === "activation_drill") {
      return (activationTable?.rows ?? []).map((r) => ({
        k: r.k,
        cells: r.cells,
        contact: r.contact ?? null,
      }));
    }
    const now = new Date();
    const m0 = startOfMonth(now).getTime();
    const m1 = endOfMonth(now).getTime();
    const d30 = Date.now() - 30 * 86400000;

    switch (drill.type) {
      case "payments_mtd":
        return payments
          .filter((p) => {
            if (!isSuccessfulPayment(p)) return false;
            const t = getPaymentTimestampMs(p);
            return t >= m0 && t <= m1;
          })
          .map((p) => ({
            k: p.id,
            cells: [
              String(p.id || "").slice(0, 14),
              `$${paymentAmountUsd(p).toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
              String(p.status || (p.paid ? "paid" : "—")),
              payEmail(p),
            ],
          }));
      case "paid_contacts": {
        const ids = paidContactIdSet(contacts, payments);
        return contacts
          .filter((c) => ids.has(c.id))
          .map((c) => ({
            k: c.id,
            cells: [c.name, c.email, String(c.healthScore ?? "—"), c.stage],
            contact: c,
          }));
      }
      case "orders_30d":
        return orders
          .filter((o) => docCreatedMs(o) >= d30)
          .map((o) => ({
            k: o.id,
            cells: [
              String(o.id || "").slice(0, 14),
              o.status || "—",
              o.user_email || o.email || String(o.user_id ?? "—"),
              o.final_price != null ? `$${Number(o.final_price).toLocaleString()}` : "—",
            ],
          }));
      case "scans_30d":
        return scans
          .filter((s) => docCreatedMs(s) >= d30)
          .map((s) => ({
            k: s.id,
            cells: [s.scan_name || s.name || s.fileName || String(s.id).slice(0, 12), s.status || "—", String(s.user_id ?? s.userId ?? "—"), ""],
          }));
      case "workspaces":
        return workspaces.map((w) => ({
          k: w.id,
          cells: [w.name || w.workspace_name || w.title || w.id, String(w.owner_id ?? w.ownerId ?? "—"), "", ""],
        }));
      case "workspace_members":
        return workspaceMembers.map((w) => ({
          k: w.id,
          cells: [String(w.workspace_id ?? w.workspaceId ?? "—"), String(w.user_id ?? w.userId ?? w.email ?? "—"), w.role || "—", ""],
        }));
      case "contacts_noninternal":
        return contacts
          .filter((c) => !c.isInternal)
          .map((c) => ({
            k: c.id,
            cells: [c.name, c.email, String(c.healthScore ?? "—"), c.stage],
            contact: c,
          }));
      default:
        return [];
    }
  }, [drill, contacts, payments, orders, scans, workspaces, workspaceMembers, funnelTable, geoTable, growthTable, deviceTable, activationTable]);

  if (!open || !drill) return null;

  const headers =
    drill.type === "geo_concentration"
      ? geoTable?.headers ?? []
      : drill.type === "funnel_drill"
      ? funnelTable?.headers ?? []
      : drill.type === "growth_drill"
        ? growthTable?.headers ?? []
        : drill.type === "device_drill"
          ? deviceTable?.headers ?? []
        : drill.type === "activation_drill"
          ? activationTable?.headers ?? []
        : drill.type === "payments_mtd"
        ? ["Payment", "Amount", "Status", "Email / ref"]
        : drill.type === "paid_contacts" || drill.type === "contacts_noninternal"
          ? ["Name", "Email", "Health", "Stage"]
          : drill.type === "orders_30d"
            ? ["Order", "Status", "User / email", "Value"]
            : drill.type === "scans_30d"
              ? ["Scan", "Status", "User", ""]
              : drill.type === "workspaces"
                ? ["Workspace", "Owner", "", ""]
                : drill.type === "workspace_members"
                  ? ["Workspace", "Member", "Role", ""]
                  : [""];

  const showContactAction =
    drill.type === "funnel_drill"
      ? Boolean(funnelTable?.showContactActions)
      : drill.type === "growth_drill"
        ? Boolean(growthTable?.showContactActions)
        : drill.type === "device_drill"
          ? Boolean(deviceTable?.showContactActions)
        : drill.type === "activation_drill"
          ? Boolean(activationTable?.showContactActions)
        : drill.type === "paid_contacts" || drill.type === "contacts_noninternal";

  const ncol = headers.length || (drill.type === "funnel_drill" ? 1 : 4);
  const colspan = ncol + (showContactAction ? 1 : 0);

  const showFinances =
    drill.type === "payments_mtd" ||
    drill.type === "paid_contacts" ||
    (drill.type === "funnel_drill" && drill.funnelKey === "ltv");

  const showCsvExport =
    (drill.type === "funnel_drill" &&
      drill.funnelKey &&
      (funnelTable?.csvExportable || COHORT_CSV_EXPORT_KEYS.has(drill.funnelKey))) ||
    (drill.type === "activation_drill" &&
      drill.stageKey &&
      (activationTable?.csvExportable || ACTIVATION_SANKEY_EXPORT_KEYS.has(drill.stageKey))) ||
    (drill.type === "device_drill" &&
      drill.deviceKey &&
      (deviceTable?.csvExportable || DEVICE_SEGMENT_EXPORT_KEYS.has(drill.deviceKey)));

  const modalWide =
    drill.type === "funnel_drill" && headers.length >= 6;

  return (
    <div className="modal-overlay" style={{ zIndex: 200 }} onClick={onClose} role="presentation">
      <div
        className="modal"
        style={{
          maxWidth: modalWide ? 960 : 720,
          width: "92vw",
          maxHeight: "85vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header" style={{ flexShrink: 0 }}>
          <div className="modal-title">{drill.title}</div>
          <button type="button" className="btn btn-ghost btn-icon" onClick={onClose}>
            ×
          </button>
        </div>
        <div style={{ padding: "0 16px 12px", flexShrink: 0, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {drill.type === "geo_concentration" && geoTable?.subtitle ? (
            <div style={{ fontSize: 12, color: "var(--text-3)", flex: "1 1 100%", lineHeight: 1.45 }}>
              {geoTable.subtitle}
            </div>
          ) : null}
          {drill.type === "geo_concentration" ? (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: "1 1 100%" }}>
              {(geoTable?.levels ?? ["country", "state", "city", "pincode"]).map((lvl) => {
                const labels = {
                  country: "Country",
                  state: "State",
                  city: "City",
                  pincode: "Pincode",
                };
                const active = geoLevel === lvl;
                return (
                  <button
                    key={lvl}
                    type="button"
                    className={active ? "btn btn-sm btn-primary" : "btn btn-sm"}
                    onClick={() => setGeoLevel(lvl)}
                  >
                    {labels[lvl] || lvl}
                  </button>
                );
              })}
            </div>
          ) : null}
          {drill.type === "funnel_drill" && funnelTable?.subtitle ? (
            <div style={{ fontSize: 12, color: "var(--text-3)", flex: "1 1 100%", lineHeight: 1.45 }}>
              {funnelTable.subtitle}
            </div>
          ) : null}
          {drill.type === "growth_drill" && growthTable?.subtitle ? (
            <div style={{ fontSize: 12, color: "var(--text-3)", flex: "1 1 100%", lineHeight: 1.45 }}>
              {growthTable.subtitle}
            </div>
          ) : null}
          {drill.type === "device_drill" && deviceTable?.subtitle ? (
            <div style={{ fontSize: 12, color: "var(--text-3)", flex: "1 1 100%", lineHeight: 1.45 }}>
              {deviceTable.subtitle}
            </div>
          ) : null}
          {drill.type === "activation_drill" && activationTable?.subtitle ? (
            <div style={{ fontSize: 12, color: "var(--text-3)", flex: "1 1 100%", lineHeight: 1.45 }}>
              {activationTable.subtitle}
            </div>
          ) : null}
          <span style={{ fontSize: 12, color: "var(--text-3)" }}>
            {rows.length} row{rows.length !== 1 ? "s" : ""}
          </span>
          {showFinances && onGoFinances ? (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => {
                onClose();
                onGoFinances("cash");
              }}
            >
              Open Finances
            </button>
          ) : null}
          {showCsvExport ? (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => {
                if (drill.type === "activation_drill") {
                  downloadActivationSankeyCsv(
                    drill.stageKey,
                    contacts,
                    workspaces,
                    workspaceMembers,
                    files,
                    orders,
                    scans,
                    productAnalyticsEvents,
                    drill.dateRange || null
                  );
                  return;
                }
                if (drill.type === "device_drill" && drill.deviceKey) {
                  downloadDeviceSegmentCsv(drill.deviceKey, contacts, {
                    includeInternal: Boolean(drill.includeInternal),
                  });
                  return;
                }
                downloadCohortCsv(drill.funnelKey, contacts, scans, orders, payments, files, productAnalyticsEvents);
              }}
            >
              ↓ Export CSV
            </button>
          ) : null}
        </div>
        <div style={{ overflow: "auto", flex: 1, padding: "0 16px 16px" }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  {headers.map((h, hi) => (
                    <th key={hi}>{h || "—"}</th>
                  ))}
                  {showContactAction ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={colspan} style={{ padding: 20, color: "var(--text-3)" }}>
                      No rows.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.k}>
                      {r.cells.map((cell, i) => (
                        <td key={i} style={{ fontSize: 13 }}>
                          {cell}
                        </td>
                      ))}
                      {showContactAction ? (
                        <td>
                          {r.contact && onOpenContact ? (
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={() => {
                                onClose();
                                onOpenContact(r.contact);
                              }}
                            >
                              Profile
                            </button>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
